import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AdminPage } from "@/pages/admin/AdminLayout";

const AdminCustomers = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [hasLeads, setHasLeads] = useState(false);
  const [hasAppts, setHasAppts] = useState(false);

  const fetchData = () => {
    const params = new URLSearchParams({ page: String(page), page_size: "25" });
    if (q) params.set("q", q);
    if (hasLeads) params.set("has_leads", "true");
    if (hasAppts) params.set("has_appointments", "true");
    api.get(`/admin/customers?${params.toString()}`).then((r) => setData(r.data)).catch(() => setData(null));
  };
  useEffect(() => { fetchData(); }, [page, hasLeads, hasAppts]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (e) => { e.preventDefault(); setPage(1); fetchData(); };

  return (
    <AdminPage title="Customers" subtitle="Every customer that ever touched the platform — registered or guest." testId="admin-customers-page">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center bg-white border border-slate-200 rounded-2xl p-4 mb-4">
        <form onSubmit={onSearch} className="flex-1 flex gap-2">
          <div className="flex-1 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, email or phone"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="admin-customers-search-input"
            />
          </div>
          <Button type="submit" className="bg-brand hover:bg-brand-hover text-white" data-testid="admin-customers-search-btn">
            Search
          </Button>
        </form>
        <label className="text-xs text-slate-600 flex items-center gap-2 shrink-0" data-testid="admin-filter-has-leads">
          <input type="checkbox" checked={hasLeads} onChange={(e) => setHasLeads(e.target.checked)} /> Has leads
        </label>
        <label className="text-xs text-slate-600 flex items-center gap-2 shrink-0" data-testid="admin-filter-has-appts">
          <input type="checkbox" checked={hasAppts} onChange={(e) => setHasAppts(e.target.checked)} /> Has appointments
        </label>
      </div>

      {!data ? (
        <div className="h-64 bg-slate-100 rounded-2xl animate-pulse" />
      ) : data.items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center">
          <Users size={28} className="mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500 text-sm">No customers match your filters.</p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-customers-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Phone</th>
                  <th className="text-right px-4 py-3 font-semibold">Leads</th>
                  <th className="text-right px-4 py-3 font-semibold">Appts</th>
                  <th className="text-right px-4 py-3 font-semibold">Showed up</th>
                  <th className="text-left px-4 py-3 font-semibold">First seen</th>
                  <th className="text-left px-4 py-3 font-semibold">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-customer-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <Link className="text-brand font-medium hover:underline" to={`/admin/customers/${encodeURIComponent(c.id)}`}>
                        {c.full_name}
                      </Link>
                      {!c.is_registered && <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-400">guest</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.email}</td>
                    <td className="px-4 py-3 text-slate-600">{c.phone || "—"}</td>
                    <td className="px-4 py-3 text-right font-medium">{c.total_leads}</td>
                    <td className="px-4 py-3 text-right">{c.total_appts}</td>
                    <td className="px-4 py-3 text-right text-emerald-700 font-medium">{c.total_showed_up}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{c.first_seen_at?.slice(0, 10) || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{c.last_activity_at?.slice(0, 10) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
            <span>Showing {data.items.length} of {data.total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</Button>
              <Button variant="outline" size="sm" disabled={data.items.length < data.page_size} onClick={() => setPage((p) => p + 1)}>Next →</Button>
            </div>
          </div>
        </>
      )}
    </AdminPage>
  );
};

export default AdminCustomers;
